import type { ConversionDeliveryResult, LeadAcceptedV1, LeadMatchData } from "./contracts";
import { createDeliveryResult } from "./contracts";
import { parseConversionEventId } from "./event-id";
import { getEnabledLeadAdapters } from "./registry";

declare global {
  interface Window {
    __srConversionEvents?: ConversionDeliveryResult[];
  }
}

const dispatchedLeadIds = new Set<string>();

function recordResults(results: ConversionDeliveryResult[]): ConversionDeliveryResult[] {
  if (typeof window === "undefined") return results;
  window.__srConversionEvents = [...(window.__srConversionEvents ?? []), ...results].slice(-100);
  return results;
}

export function recordLeadTrackingSkipped(eventId: string, reason: string): ConversionDeliveryResult[] {
  return recordResults([
    createDeliveryResult({
      provider: "dispatcher",
      channel: "browser",
      eventName: "lead_accepted",
      status: "skipped",
      eventId,
      reason,
    }),
  ]);
}

export async function trackLeadAccepted(event: LeadAcceptedV1, matchData?: LeadMatchData): Promise<ConversionDeliveryResult[]> {
  const eventId = parseConversionEventId(event.conversionEventId);
  if (!eventId) return recordLeadTrackingSkipped("invalid", "invalid_conversion_event_id");
  if (dispatchedLeadIds.has(eventId)) return recordLeadTrackingSkipped(eventId, "duplicate_dispatch");

  dispatchedLeadIds.add(eventId);
  const adapters = getEnabledLeadAdapters();
  if (adapters.length === 0) return recordLeadTrackingSkipped(eventId, "no_enabled_provider");

  const settled = await Promise.allSettled(adapters.map((adapter) => adapter.trackLead(event, matchData)));
  const results = settled.map((result, index) => {
    if (result.status === "fulfilled") return result.value;
    return createDeliveryResult({
      provider: adapters[index].id,
      channel: "browser",
      eventName: "lead_accepted",
      status: "failed",
      eventId,
      reason: "adapter_error",
    });
  });

  return recordResults(results);
}
